// TODO(nav-v3): remove this drawer (and the `notifications-drawer/` folder)
// once Nav v2 is retired. Nav v3 uses `NotificationsDropdown` instead.
// `NotificationFeed` and `NotificationItem` should also be deleted unless
// reused by `NotificationsDropdown` at that time.
import { type FunctionComponent, useCallback, useEffect, useRef } from 'react'
import { NotificationDrawer as TDSNotificationDrawer } from '@retailer-platform/shared-components/src/tds'
import { useHistory } from 'react-router-dom'
import { usePartnerContext, useCurrentAccountContext } from '@retailer-platform/dashboard/utils'
import { StringParam, useQueryParam } from 'use-query-params'
import { trackEvent } from '@retailer-platform/dashboard/analytics'
import { routes } from '../../routing/routes'
import { useDomainMessages } from '../../utils/domain/intl'
import { NotificationFilterType } from '../../hooks/useNotificationFeed'
import { NotificationFeed } from './NotificationFeed'

interface NotificationsDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export const NotificationsDrawer: FunctionComponent<NotificationsDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const history = useHistory()
  const partnerContext = usePartnerContext()
  const { account } = useCurrentAccountContext()
  const [activeFilter, setActiveFilter] = useQueryParam('filter', StringParam)
  const previousFilterRef = useRef<string | null>(null)

  const i18n = useDomainMessages({
    title: 'notificationsDomain.drawer.title',
    settingsButton: 'notificationsDomain.drawer.button.settings',
    closeButton: 'notificationsDomain.drawer.button.close',
    all: 'notificationsDomain.drawer.filter.all',
    urgent: 'notificationsDomain.drawer.filter.urgent',
    unread: 'notificationsDomain.drawer.filter.unread',
    today: 'notificationsDomain.drawer.filter.today',
    earlier: 'notificationsDomain.drawer.filter.earlier',
  })

  const filters = [
    { label: i18n.all, value: NotificationFilterType.All },
    { label: i18n.urgent, value: NotificationFilterType.Urgent },
    { label: i18n.unread, value: NotificationFilterType.Unread },
    { label: i18n.today, value: NotificationFilterType.Today },
    { label: i18n.earlier, value: NotificationFilterType.Earlier },
  ]

  // Track drawer open/close events
  useEffect(() => {
    if (isOpen) {
      trackEvent({
        id: 'notifications.drawer.opened',
        description: 'User opened the notifications drawer',
        data: {
          partnerId: partnerContext.partnerId,
          accountId: account.id,
          timestamp: new Date().toISOString(),
        },
      })
    }
  }, [isOpen, partnerContext.partnerId, account.id])

  const onSettingsClick = useCallback(() => {
    if (partnerContext.partnerId) {
      trackEvent({
        id: 'notifications.drawer.settings.clicked',
        description: 'User clicked settings button from drawer',
        data: {
          partnerId: partnerContext.partnerId,
          accountId: account.id,
          currentFilter: activeFilter || NotificationFilterType.All,
          timestamp: new Date().toISOString(),
        },
      })
      history.push(
        routes['notifications-settings'].replace(':partner_id', partnerContext.partnerId)
      )
      onClose()
    }
  }, [history, partnerContext.partnerId, activeFilter, account.id, onClose])

  const handleFilterChange = useCallback(
    (value: string) => {
      const previousFilter = previousFilterRef.current || activeFilter
      trackEvent({
        id: 'notifications.drawer.filter.selected',
        description: 'User selected a notification filter',
        data: {
          filterType: value,
          previousFilter,
          partnerId: partnerContext.partnerId,
          accountId: account.id,
          timestamp: new Date().toISOString(),
        },
      })
      previousFilterRef.current = activeFilter || null
      setActiveFilter(value)
    },
    [setActiveFilter, activeFilter, partnerContext.partnerId, account.id]
  )

  return (
    <TDSNotificationDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.title}
      zIndex={299}
      onSettingsClick={onSettingsClick}
      selectedFilter={activeFilter || NotificationFilterType.All}
      onFilterChange={handleFilterChange}
      filters={filters}
    >
      <NotificationFeed
        account={account}
        filter={(activeFilter as NotificationFilterType) || NotificationFilterType.All}
        filterLabel={
          filters.find(f => f.value === (activeFilter || NotificationFilterType.All))?.label || ''
        }
        pollingInterval={isOpen ? 30000 : 0}
        isOpen={isOpen}
      />
    </TDSNotificationDrawer>
  )
}
